import { useState, useRef, useEffect } from 'react';
import { Download, Upload, CheckCircle2, FileText, Trash2, Loader2, PenLine, Bookmark } from 'lucide-react';
import { api } from '../lib/api';
import { API_BASE } from '../lib/apiBase';
import ConfirmDialog from './admin/ConfirmDialog';
import { esfAnBerichtHaengen } from '../lib/esfAnhaengen';

const ESF_CATEGORY = 'ESF Fragebogen';

interface UploadedFile {
  id: number;
  name: string;
  originalName: string;
  size: number;
  mimeType: string;
  category: string;
  reportId: number;
  createdAt: string;
}

interface ESFQuestionnaireProps {
  darkMode: boolean;
  reportId?: number | string;
  onFileChange?: (file: UploadedFile | null) => void;
  onEnabledChange?: (enabled: boolean) => void;
}

export function ESFQuestionnaire({ darkMode, reportId, onFileChange, onEnabledChange }: ESFQuestionnaireProps) {
  const [enabled, setEnabled] = useState(true);
  const [esfFile, setEsfFile] = useState<UploadedFile | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [alertMsg, setAlertMsg] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showVorlageConfirm, setShowVorlageConfirm] = useState(false);
  // Hat das Team schon eine eigene ESF-Vorlage? null = noch unbekannt.
  const [eigeneVorlage, setEigeneVorlage] = useState<boolean | null>(null);
  const ladeVorlageStatus = () => {
    api.get<{ eigene: boolean }>('/api/esf-template/team')
      .then(r => setEigeneVorlage(!!r.eigene))
      .catch(() => {});
  };
  // Beim Laden und beim Zurückkommen aus dem Formular-Tab (dort wird die Vorlage gespeichert).
  useEffect(() => {
    ladeVorlageStatus();
    window.addEventListener('focus', ladeVorlageStatus);
    return () => window.removeEventListener('focus', ladeVorlageStatus);
  }, []);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch ESF file from server; onlyPositive=true means we only update state if a file IS found
  const fetchEsfFile = (onlyPositive = false) => {
    if (reportId == null) return;
    api.get<UploadedFile[]>(`/api/reports/${reportId}/files`)
      .then((files) => {
        // Take the most recently uploaded ESF file (highest id) in case duplicates exist
        const esfFiles = files.filter((f) => f.category === ESF_CATEGORY);
        const esf = esfFiles.length > 0
          ? esfFiles.reduce((a, b) => (a.id > b.id ? a : b))
          : null;
        if (esf) {
          setEsfFile(esf);
          setEnabled(true);
          onFileChange?.(esf);
        } else if (!onlyPositive) {
          setEsfFile(null);
        }
      })
      .catch(() => {});
  };

  // On mount / when reportId changes: check if an ESF file already exists
  // Retry once after 1.5s in case the server is still processing
  useEffect(() => {
    if (reportId == null) return;
    fetchEsfFile(false);
    const timer = setTimeout(() => fetchEsfFile(true), 1500);
    return () => clearTimeout(timer);
  }, [reportId]);

  // Re-check when window regains focus (user comes back from the fill-in tab)
  // onlyPositive=true: only update state if a NEW file was found (never clears existing state)
  useEffect(() => {
    const handleFocus = () => fetchEsfFile(true);
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') fetchEsfFile(true);
    };
    // Listen for localStorage signal from fill tab after successful save
    const handleStorage = (e: StorageEvent) => {
      if (e.key === `esf_saved_${reportId}`) {
        fetchEsfFile(false); // full refresh — file is guaranteed to exist now
      }
    };
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('storage', handleStorage);
    };
  }, [reportId]);

  const handleUpload = async (file: File) => {
    if (reportId == null || isUploading) return;
    if (file.type !== 'application/pdf') {
      setAlertMsg('Bitte nur PDF-Dateien hochladen.');
      return;
    }
    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', ESF_CATEGORY);
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API_BASE}/api/reports/${reportId}/files`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        console.error('ESF Upload fehlgeschlagen:', body.message ?? res.status);
        return;
      }
      const uploaded: UploadedFile = await res.json();
      setEsfFile(uploaded);
      onFileChange?.(uploaded);
    } catch (err) {
      console.error('ESF Upload-Fehler:', err);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async () => {
    if (!esfFile) return;
    try {
      await api.delete(`/api/files/${esfFile.id}`);
      setEsfFile(null);
      onFileChange?.(null);
    } catch (err) {
      console.error('ESF Löschen fehlgeschlagen:', err);
    }
    setShowDeleteConfirm(false);
  };

  const handleDownloadUploaded = async () => {
    if (!esfFile) return;
    const token = localStorage.getItem('auth_token');
    try {
      const res = await fetch(`${API_BASE}/api/files/${esfFile.id}/download`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = esfFile.originalName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download fehlgeschlagen:', err);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // ESF-Vorlage des Teams (im Formular per „Als Vorlage speichern“ gesetzt; ohne eigene
  // Vorlage das Standard-Set) ausfüllen und direkt anhängen — ohne Formular-Tab.
  const vorlageUebernehmen = async () => {
    if (reportId == null || isGenerating) return;
    setShowVorlageConfirm(false);
    setIsGenerating(true);
    try {
      const { selections } = await api.get<{ selections: string[]; eigene: boolean }>('/api/esf-template/team');
      try { localStorage.setItem(`esf_checked_${reportId}`, JSON.stringify(selections)); } catch { /* ignore */ }
      await esfAnBerichtHaengen(reportId, selections);
      fetchEsfFile();
    } catch {
      setAlertMsg('Vorlage konnte nicht übernommen werden.');
    } finally {
      setIsGenerating(false);
    }
  };

  const card = darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200';
  const subtle = darkMode ? 'text-slate-400' : 'text-slate-500';

  return (
    <div className={`rounded-xl border shadow-sm ${card}`}>
      {/* Header row with toggle */}
      <div className="p-4 sm:p-6 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className={`w-9 h-9 shrink-0 rounded-lg flex items-center justify-center ${
            enabled ? 'bg-blue-600' : darkMode ? 'bg-slate-700' : 'bg-slate-100'
          }`}>
            <FileText className={`w-4 h-4 ${enabled ? 'text-white' : subtle}`} />
          </div>
          <div className="min-w-0">
            <h3 className={`text-lg font-semibold break-words ${darkMode ? 'text-white' : 'text-slate-900'}`}>
              ESF Fragebogen anhängen
            </h3>
            <p className={`text-xs mt-0.5 break-words ${subtle}`}>
              Fragebogen – Bereichsübergreifende Grundsätze des ESF Plus
            </p>
          </div>
        </div>

        {/* Toggle switch */}
        <button
          onClick={() => setEnabled((v) => { const next = !v; onEnabledChange?.(next); return next; })}
          className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none ${
            enabled ? 'bg-blue-600' : darkMode ? 'bg-slate-600' : 'bg-slate-300'
          }`}
          role="switch"
          aria-checked={enabled}
        >
          <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
            enabled ? 'translate-x-6' : 'translate-x-1'
          }`} />
        </button>
      </div>

      {/* Expanded content */}
      {enabled && (
        <div className={`border-t px-4 sm:px-6 pb-5 pt-4 ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>

          {/* Step 1: Fill in browser → auto-attach */}
          <div className="flex items-start gap-3 mb-5">
            <div className={`flex-shrink-0 w-6 h-6 rounded-full text-xs font-bold flex items-center justify-center mt-0.5 ${
              darkMode ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600'
            }`}>1</div>
            <div className="flex-1">
              <p className={`text-sm font-medium mb-1 ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                Formular ausfüllen &amp; direkt anhängen
              </p>
              <p className={`text-xs mb-3 ${subtle}`}>
                Öffnet das Formular in einem neuen Tab. Einfach anklicken, was zutrifft — kein Download, kein Upload nötig.
              </p>
              {reportId == null ? (
                <p className={`text-xs px-3 py-2 rounded-lg border ${
                  darkMode ? 'bg-amber-900/20 border-amber-700 text-amber-300' : 'bg-amber-50 border-amber-200 text-amber-800'
                }`}>
                  Speichern Sie den Bericht zuerst, um das Formular auszufüllen.
                </p>
              ) : (
                <>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => window.open(`/esf-ausfuellen?reportId=${reportId}`, '_blank')}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border transition-colors bg-blue-600 border-blue-600 text-white hover:bg-blue-700"
                  >
                    <PenLine className="w-4 h-4" />
                    {esfFile ? 'Jetzt ändern' : 'Jetzt ausfüllen'}
                  </button>
                  <button
                    onClick={() => (
                      eigeneVorlage === false
                        ? window.open(`/esf-ausfuellen?reportId=${reportId}&vorlage=1`, '_blank')
                        : esfFile ? setShowVorlageConfirm(true) : vorlageUebernehmen()
                    )}
                    disabled={isGenerating}
                    className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                      darkMode
                        ? 'border-slate-600 text-slate-300 hover:bg-slate-700'
                        : 'border-slate-300 text-slate-700 hover:bg-slate-50'
                    }`}
                    title={eigeneVorlage === false
                      ? 'Noch keine Vorlage gespeichert — öffnet das Formular zum Anlegen'
                      : 'Fragebogen mit Ihrer Vorlage ausfüllen und direkt anhängen'}
                  >
                    {isGenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bookmark className="w-4 h-4" />}
                    {eigeneVorlage === false ? 'Vorlage anlegen' : 'Vorlage übernehmen'}
                  </button>
                </div>
                {eigeneVorlage === false && (
                  <p className={`text-xs mt-2 ${subtle}`}>
                    Noch keine Vorlage gespeichert. Formular öffnen, ankreuzen und oben „Als Vorlage speichern“ klicken — danach füllt „Vorlage übernehmen“ jeden Bericht mit einem Klick.
                  </p>
                )}
                </>
              )}
            </div>
          </div>

          {/* Step 2: Upload filled form */}
          <div className="flex items-start gap-3">
            <div className={`flex-shrink-0 w-6 h-6 rounded-full text-xs font-bold flex items-center justify-center mt-0.5 ${
              esfFile
                ? 'bg-green-500 text-white'
                : darkMode ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600'
            }`}>
              {esfFile ? <CheckCircle2 className="w-4 h-4" /> : '2'}
            </div>
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-medium mb-1 ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                Anhang — ausgefüllte PDF
              </p>

              {reportId == null && (
                <p className={`text-xs px-3 py-2 rounded-lg border ${
                  darkMode
                    ? 'bg-amber-900/20 border-amber-700 text-amber-300'
                    : 'bg-amber-50 border-amber-200 text-amber-800'
                }`}>
                  Speichern Sie den Bericht zuerst, um Dateien hochzuladen.
                </p>
              )}

              {reportId != null && !esfFile && (
                <div
                  onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); }}
                  onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); }}
                  onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsDragging(false);
                    const file = e.dataTransfer.files[0];
                    if (file) handleUpload(file);
                  }}
                  onClick={() => fileInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-lg px-4 py-5 text-center cursor-pointer transition-colors ${
                    isDragging
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                      : darkMode
                        ? 'border-slate-600 hover:border-blue-500 hover:bg-slate-700/50'
                        : 'border-slate-300 hover:border-blue-400 hover:bg-slate-50'
                  }`}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleUpload(file);
                    }}
                  />
                  {isUploading ? (
                    <div className="flex flex-col items-center gap-2">
                      <Loader2 className={`w-6 h-6 animate-spin ${subtle}`} />
                      <p className={`text-xs ${subtle}`}>Wird hochgeladen…</p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2">
                      <Upload className={`w-5 h-5 ${subtle}`} />
                      <p className={`text-xs ${subtle}`}>
                        Ausgefüllte PDF hier ablegen oder klicken zum Auswählen
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Uploaded file display */}
              {esfFile && (
                <div className={`flex items-center gap-3 p-3 rounded-lg border mt-1 ${
                  darkMode ? 'bg-green-900/10 border-green-800' : 'bg-green-50 border-green-200'
                }`}>
                  <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium truncate ${darkMode ? 'text-green-300' : 'text-green-800'}`}>
                      {esfFile.originalName}
                    </p>
                    <p className={`text-xs ${darkMode ? 'text-green-500' : 'text-green-600'}`}>
                      {formatFileSize(esfFile.size)} · {new Date(esfFile.createdAt).toLocaleDateString('de-DE')}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={handleDownloadUploaded}
                      className={`p-1.5 rounded-md transition-colors ${
                        darkMode ? 'hover:bg-green-900/30 text-green-400' : 'hover:bg-green-100 text-green-700'
                      }`}
                      title="Herunterladen"
                    >
                      <Download className="w-4 h-4" />
                    </button>
                    {showDeleteConfirm ? (
                      <div className="flex items-center gap-1">
                        <button
                          onClick={handleDelete}
                          className="px-2 py-1 text-xs font-medium text-white bg-red-500 hover:bg-red-600 rounded-md transition-colors"
                        >
                          Entfernen
                        </button>
                        <button
                          onClick={() => setShowDeleteConfirm(false)}
                          className={`px-2 py-1 text-xs font-medium rounded-md transition-colors ${
                            darkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-600 hover:bg-slate-100'
                          }`}
                        >
                          Abbrechen
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setShowDeleteConfirm(true)}
                        className={`p-1.5 rounded-md transition-colors ${
                          darkMode ? 'hover:bg-red-900/30 text-slate-400 hover:text-red-400' : 'hover:bg-red-50 text-slate-500 hover:text-red-600'
                        }`}
                        title="Entfernen"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      <ConfirmDialog
        open={showVorlageConfirm}
        title="Vorlage übernehmen?"
        message="Der angehängte Fragebogen wird durch die Antworten aus Ihrer Vorlage ersetzt."
        confirmLabel="Ersetzen"
        onConfirm={vorlageUebernehmen}
        onCancel={() => setShowVorlageConfirm(false)}
      />
      <ConfirmDialog
        open={alertMsg !== null}
        title="Hinweis"
        message={alertMsg || ''}
        confirmLabel="OK"
        hideCancel
        onConfirm={() => setAlertMsg(null)}
        onCancel={() => setAlertMsg(null)}
      />
    </div>
  );
}
